import styles from '@components/components/Countdown/Countdown.module.scss';
import { Button, Heading, Link } from '@wienenergiegithub/ui-next';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useTranslation } from '@wienenergiegithub/modules-next/hooks';

const Countdown = ({
  timestamp,
  isMinimal,
  title,
  subtitle,
  ctas,
  image,
  location,
  showLocation,
  showEnddate,
  hasBadge,
  badgeText,
  badgeVariant,
  expiredTitle,
  expiredSubtitle,
  expiredLink,
  expiredImage,
}) => {
  const expiredCta = [
    { link: { title: expiredLink?.title, url: expiredLink?.url } },
  ];

  const formattedDate = DateTime.fromMillis(timestamp)
    .setLocale('de-AT')
    .toFormat('dd. MMMM yyyy, HH:mm');

  const checkExpiry = inputTimestamp => {
    const currentTimeInMilliseconds = new Date().getTime();
    const isExpired = currentTimeInMilliseconds > inputTimestamp;
    return isExpired;
  };

  const calculateTimeLeft = inputTimestamp => {
    const difference = inputTimestamp - +new Date().getTime();
    let time = {};
    if (difference > 0) {
      time = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
      if (time.seconds < 10) {
        time.seconds = `0${time.seconds.toString()}`;
      }
      if (time.minutes < 10) {
        time.minutes = `0${time.minutes.toString()}`;
      }
      if (time.hours < 10) {
        time.hours = `0${time.hours.toString()}`;
      }
    }
    return time;
  };

  const [isExpired, setIsExpired] = useState(checkExpiry(timestamp));
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(timestamp));

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft(timestamp));
      setIsExpired(checkExpiry(timestamp));
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  });

  return (
    <div className={styles.outerWrapper}>
      <CountdownBackground
        expiredImage={expiredImage}
        image={image}
        isExpired={isExpired}
      />
      {isExpired ? (
        <div className={styles.expiredCountdown}>
          <CountdownHeader
            ctas={expiredCta}
            subtitle={expiredSubtitle}
            title={expiredTitle}
          />
        </div>
      ) : (
        <div className={styles.countdownWrapper}>
          {hasBadge && (
            <CountdownBadge
              badgeText={badgeText}
              badgeVariant={badgeVariant}
              isMinimal={isMinimal}
            />
          )}
          <CountdownHeader ctas={ctas} subtitle={subtitle} title={title} />
          {isMinimal ? (
            <CounterDaysOnly days={timeLeft.days} />
          ) : (
            <div className={styles.sectionWrapper}>
              <Counter
                days={timeLeft.days}
                enddate={formattedDate}
                hours={timeLeft.hours}
                location={location}
                minutes={timeLeft.minutes}
                seconds={timeLeft.seconds}
                showEnddate={showEnddate}
                showLocation={showLocation}
              />
              {ctas?.length > 1 && <CountdownLinks ctas={ctas.slice(1)} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CountdownHeader = ({ title, subtitle, ctas }) => (
  <div className={styles.countdownHeader}>
    <div className={styles.title}>{title}</div>
    <Heading className={styles.subtitle} size="h1" theme="contrast">
      {subtitle}
    </Heading>
    <Button render={<Link to={ctas?.[0].link?.url} />} theme="ghost">
      {ctas?.[0]?.link?.title}
    </Button>
  </div>
);

const CountdownLinks = ({ ctas }) => {
  const { t } = useTranslation('countdown');
  return (
    <div className={styles.linkWrapper}>
      <div className={styles.moreInfos}>{t('infos')}</div>
      {ctas?.map(ctaItem => (
        <div className={styles.link} key={ctaItem?.link?.title}>
          <Button
            render={<Link to={ctaItem?.link?.url} />}
            theme="ghost"
            variant="link"
          >
            {ctaItem?.link?.title}
          </Button>
        </div>
      ))}
    </div>
  );
};

const CountdownBackground = ({ image, expiredImage, isExpired }) =>
  (!isExpired && image) || (isExpired && expiredImage) ? (
    <div>
      <div className={styles.organicBlue} />
      <div
        alt=""
        className={styles.backgroundImage}
        style={{
          backgroundImage: `url(${isExpired ? expiredImage.url : image.url})`,
        }}
      />
    </div>
  ) : (
    <div>
      <div
        className={classNames({
          [styles.runningBackground]: !isExpired,
          [styles.expiredBackground]: isExpired,
        })}
      />
    </div>
  );

const Counter = ({
  days,
  hours,
  minutes,
  seconds,
  enddate,
  location,
  showEnddate,
  showLocation,
}) => {
  const { t } = useTranslation('countdown');
  return (
    <div>
      <div className={styles.counterWrapper}>
        <div className={styles.unitWrapper}>
          <div className={styles.numbers}>{days}</div>
          <div className={styles.units}>
            {t('day', { n: parseInt(days, 10) })}
          </div>
        </div>
        <div className={styles.colon}>:</div>
        <div className={styles.unitWrapper}>
          <div className={styles.numbers}>{hours}</div>
          <div className={styles.units}>
            {t('hour', { n: parseInt(hours, 10) })}
          </div>
        </div>
        <div className={styles.colon}>:</div>
        <div className={styles.unitWrapper}>
          <div className={styles.numbers}>{minutes}</div>
          <div className={styles.units}>
            {t('minute', { n: parseInt(minutes, 10) })}
          </div>
        </div>
        <div className={styles.colon}>:</div>
        <div className={styles.unitWrapper}>
          <div className={styles.numbers}>{seconds}</div>
          <div className={styles.units}>
            {t('second', { n: parseInt(seconds, 10) })}
          </div>
        </div>
      </div>
      <Info
        enddate={enddate}
        location={location}
        showEnddate={showEnddate}
        showLocation={showLocation}
      />
    </div>
  );
};

const CounterDaysOnly = ({ days }) => {
  const { t } = useTranslation('countdown');
  return (
    <div className={styles.daysWrapper}>
      <div>{t('daysleftprefix')}</div>
      <div className={styles.days}>{days + 1}</div>
      <div> {t('day', { n: parseInt(days, 10) })}</div>
    </div>
  );
};

const Info = ({ enddate, location, showEnddate, showLocation }) => {
  const { t } = useTranslation('countdown');
  return (
    (showEnddate || showLocation) && (
      <div className={styles.dateAndLocation}>
        {showEnddate && (
          <div>
            <div className={styles.moreInfos}>{t('endofsign')}</div>
            <div className={styles.enddate}>{enddate}</div>
          </div>
        )}
        {showLocation && (
          <div>
            <div className={styles.moreInfos}>{t('area')}</div>
            <div className={styles.location}>{location}</div>
          </div>
        )}
      </div>
    )
  );
};

const CountdownBadge = ({ badgeText, badgeVariant, isMinimal }) => (
  <Badge isMinimal={isMinimal} theme={badgeVariant}>
    {badgeText}
  </Badge>
);

const Badge = ({ theme, children, className, isMinimal }) => (
  <div
    className={classNames(className, styles[theme], styles.badge, {
      [styles.minimal]: isMinimal,
    })}
  >
    <div>{children}</div>
  </div>
);

export default Countdown;
